import React from 'react';
import {
  Checkbox,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Badge,
  Avatar,
  ListItemAvatar
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { metersToMiles } from '../../lib/utils';
import useStores from '../../stores/useStores';
import DataGridItem from '../dataGrid/DataGridItem';
import BuildingCardMeta from './BuildingCardMeta';
import './BuildingCard.sass';

// variant = ['teaser, 'compact']

const BuildingCard = ({
  to = null,
  disableGrade = false,
  projectId,
  building,
  variant,
  onClick = () => {},
  ...rest
}) => {
  const history = useHistory();
  const {
    name,
    address,
    financials,
    id,
    property_type,
    recommendations,
    der_score,
    der_accuracy,
    distance,
    building_name,
    area_in_sq_meters
  } = building;

  const distanceInMiles = metersToMiles(distance * 1000);
  const title = distance ? (
    <>
      {building_name || name || address}{' '}
      <span style={{ opacity: 0.8 }}>{distanceInMiles}mi.</span>
    </>
  ) : (
    building_name || name || address
  );
  const {
    buildingStore,
    uiStore,
    mapStore,
    userStore,
    projectDetailStore,
    editorStore
  } = useStores();
  const { controlPanelState } = uiStore;
  const {
    setHoveredBuildingCard,
    setSelectedBuilding,
    selectedBuilding
  } = editorStore;

  const showGrade =
    !disableGrade &&
    der_score &&
    userStore.isAdmin &&
    variant === undefined &&
    controlPanelState !== 'picker';

  const gradeColor = 'rgb(76, 175, 80)';
  const isPicked = buildingStore.pickedResults.has(id);
  const financialData =
    building.financials && variant === 'teaser'
      ? financials.slice(0, 4)
      : [...new Array(4)].map(() => {
          return {
            value: 1,
            isLoading: true
          };
        });

  const className =
    history.location.pathname.includes(`/${id}`) || selectedBuilding?.id === id
      ? `building-card building-card-active`
      : `building-card`;

  return (
    <>
      <ListItem
        className={className}
        button
        // component={NavLink}
        ContainerComponent="div"
        onClick={() => {
          // TODO Should move this to UiStore
          if (variant === 'upload') {
            setSelectedBuilding(building);
            return;
          }
          projectDetailStore.setVariant('large');
          mapStore.setPopup(null);
          history.push(to || `/search/${building.id}`);
          onClick(event);
        }}
        onMouseEnter={() => {
          if (variant === 'upload') {
            setHoveredBuildingCard(building);
          }
          const url = window.location.href;
          const end = url.substring(url.length - 6);
          if ((!variant && end === 'search') || end === 'earch/') {
            mapStore.setPopup(building);
          }
        }}
        onMouseLeave={() => {
          mapStore.setPopup(null);
        }}
        {...rest}
      >
        {showGrade && (
          <ListItemAvatar>
            <Badge
              classes={{
                badge: 'building-card-avatar-badge'
              }}
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              badgeContent={
                <Typography
                  className="building-card-avatar-badge-text"
                  variant="caption"
                >
                  {der_accuracy}%
                </Typography>
              }
            >
              <Avatar
                style={{
                  border: `1px solid ${gradeColor}`,
                  transition: 'all 0.1s ease-out',
                  transitionProperty: 'background, color, border-color',
                  color: gradeColor,
                  backgroundColor: 'transparent'
                }}
                className="search-card-avatar"
              >
                {der_score}
              </Avatar>
            </Badge>
          </ListItemAvatar>
        )}
        {controlPanelState === 'picker' && (
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={isPicked}
              tabIndex={-1}
              disableRipple
              onClick={event => {
                if (controlPanelState === 'picker') {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!isPicked) {
                    buildingStore.pickResult(id);
                  } else {
                    buildingStore.unPickResult(id);
                  }
                }
              }}
            />
          </ListItemIcon>
        )}
        <ListItemText
          className="building-card-content"
          disableTypography
          inset={variant === 'compact'}
          primary={
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} lg>
                <div className="building-card-primary">
                  {(recommendations || property_type || area_in_sq_meters) && (
                    <BuildingCardMeta
                      recommendations={recommendations}
                      propertyType={property_type}
                      // propertyType={' '}
                      area={area_in_sq_meters}
                    />
                  )}
                  <Typography
                    color={
                      variant === 'compact' ? 'textSecondary' : 'textPrimary'
                    }
                    variant={variant === 'compact' ? 'body2' : 'body1'}
                    noWrap={variant !== 'teaser'}
                  >
                    {building_name || title}
                  </Typography>
                </div>
              </Grid>
              {variant === 'teaser' && (
                <Grid item xs={12} lg={5}>
                  <Grid container spacing={0} className="data-grid compact">
                    {financialData.map(item => (
                      <DataGridItem
                        key={item.label}
                        {...item}
                        xs={6}
                        sm={3}
                        md={6}
                      />
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
          }
        />
        {variant === 'teaser' && (
          <ListItemSecondaryAction className="building-card-icon">
            <NavigateNextIcon size="large" />
          </ListItemSecondaryAction>
        )}
      </ListItem>
      <Divider light variant="inset" />
    </>
  );
};

export default observer(BuildingCard);
